export type JsonObject = Record<string, unknown>;

export function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value: unknown): value is JsonObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function expectObject(value: unknown, context: string): JsonObject {
  if (!isPlainObject(value)) {
    throw new Error(`Expected ${context} to be an object`);
  }

  return value;
}

export function readString(
  value: JsonObject,
  key: string,
  fallback = ""
): string {
  const entry = value[key];
  return typeof entry === "string" ? entry : fallback;
}

export function readStringEnum<T extends string>(
  value: JsonObject,
  key: string,
  allowed: readonly T[],
  fallback: T
): T {
  const entry = value[key];
  return typeof entry === "string" && allowed.includes(entry as T)
    ? (entry as T)
    : fallback;
}

export function readNullableString(
  value: JsonObject,
  key: string
): string | null {
  const entry = value[key];
  if (entry === null) {
    return null;
  }

  return typeof entry === "string" ? entry : null;
}

export function readOptionalNumber(
  value: JsonObject,
  key: string
): number | null {
  const entry = value[key];
  return typeof entry === "number" ? entry : null;
}

export function readBoolean(
  value: JsonObject,
  key: string,
  fallback: boolean
): boolean {
  const entry = value[key];
  return typeof entry === "boolean" ? entry : fallback;
}

export function readStringArray(value: JsonObject, key: string): string[] {
  const entry = value[key];
  if (!Array.isArray(entry)) {
    return [];
  }

  return entry.filter((item): item is string => typeof item === "string");
}

export function readUnknownArray(value: JsonObject, key: string): unknown[] {
  const entry = value[key];
  return Array.isArray(entry) ? cloneValue(entry) : [];
}

export function readUnknownRecord(
  value: JsonObject,
  key: string
): JsonObject | null {
  const entry = value[key];
  return isPlainObject(entry) ? cloneValue(entry) : null;
}

export function readObjectArray(value: JsonObject, key: string): JsonObject[] {
  const entry = value[key];
  if (!Array.isArray(entry)) {
    return [];
  }

  return entry.map((item, index) => expectObject(item, `${key}[${index}]`));
}

export function splitKnown(
  value: JsonObject,
  knownKeys: readonly string[]
): JsonObject {
  const extras: JsonObject = {};

  Object.entries(value).forEach(([key, entry]) => {
    if (!knownKeys.includes(key)) {
      extras[key] = cloneValue(entry);
    }
  });

  return extras;
}
